/**
 * PretextOverflowRule, Replaces the estimateLineCount heuristic with
 * Pretext's accurate text measurement for overflow detection and font
 * size reduction.
 *
 * Drop-in replacement for OverflowRule in CompilationRules.ts
 *
 * @packageDocumentation
 * @module ai/compiler
 */

import { prepare, layout } from '@chenglou/pretext';
import type { PreparedText } from '@chenglou/pretext';
import TextFlowObject from '../../model/TextFlowObject';
import ShapeFlowObject from '../../model/ShapeFlowObject';
import ImageFlowObject from '../../model/ImageFlowObject';
import FontAttrsArray from '../../model/FontAttrsArray';
import { LayoutSlot, frameToPixels } from '../schemas/LayoutTemplate';
import { ContentBlock } from '../schemas/ContentBlock';
import type { ScreenCompilationContext, CompilationRule } from './CompilationRules';

/**
 * Use Pretext to accurately measure text height within a slot,
 * reducing font size if text overflows.
 *
 * Replaces the old heuristic (avgCharWidth = fontSize * 0.5) with
 * actual browser-measured text metrics.
 */
export class PretextOverflowRule implements CompilationRule {
  name = 'PretextOverflowRule';
  priority = 40; // Same as original OverflowRule

  apply(context: ScreenCompilationContext): void {
    for (const mmo of context.contentMMOs) {
      if (!(mmo instanceof TextFlowObject)) continue;

      // Skip blocks that handle their own sizing
      const block = context.blockForMMO.get(mmo.id);
      if (block && (block.type === 'toc-list' || block.type === 'timeline')) continue;

      const slot = context.slotForMMO.get(mmo.id);
      if (!slot) continue;

      const slotFrame = frameToPixels(slot.frame, context.viewSize);
      const lineHeightMultiplier = context.theme.typography.lineHeight || 1.4;
      const minFontSize = slot.constraints?.minFontSize || 12;

      let fontSize = mmo.font.fontSize;
      const fontFamily = mmo.font.fontFamily || 'Inter';

      // Use Pretext for accurate measurement
      const fontStr = `${fontSize}px ${fontFamily}`;
      let prepared: PreparedText;

      try {
        prepared = prepare(mmo.text, fontStr);
      } catch (e) {
        // Fall back to old heuristic if Pretext fails (e.g., font not loaded)
        context.warnings.push(
          `Screen ${context.screenIndex + 1}: PretextOverflowRule falling back to heuristic for "${mmo.text.substring(0, 30)}..."`
        );
        return;
      }

      const lineHeight = fontSize * lineHeightMultiplier;
      let result = layout(prepared, slotFrame.width, lineHeight);

      // If it fits, nothing to do
      if (result.height <= slotFrame.height) continue;

      // Reduce font size until it fits (or we hit minimum)
      while (result.height > slotFrame.height && fontSize > minFontSize) {
        fontSize -= 1;
        const newFontStr = `${fontSize}px ${fontFamily}`;
        prepared = prepare(mmo.text, newFontStr);
        const newLineHeight = fontSize * lineHeightMultiplier;
        result = layout(prepared, slotFrame.width, newLineHeight);
      }

      if (fontSize === mmo.font.fontSize) continue;

      fontSize = Math.max(fontSize, minFontSize);
      mmo.changeFontSize(fontSize);

      context.warnings.push(
        `Screen ${context.screenIndex + 1}: PretextOverflowRule reduced font from ${mmo.font.fontSize}pt to ${fontSize}pt (accurate measurement)`
      );
    }
  }
}

/**
 * PretextVerticalCenterRule, Uses Pretext for accurate text height
 * measurement when centering text within slots.
 *
 * Replaces VerticalCenterRule's estimateLineCount heuristic.
 */
export class PretextVerticalCenterRule implements CompilationRule {
  name = 'PretextVerticalCenterRule';
  priority = 20; // Same as original VerticalCenterRule

  apply(context: ScreenCompilationContext): void {
    for (const mmo of context.contentMMOs) {
      if (!(mmo instanceof TextFlowObject)) continue;

      const block = context.blockForMMO.get(mmo.id);
      if (block && (block.type === 'toc-list' || block.type === 'timeline')) continue;

      const slot = context.slotForMMO.get(mmo.id);
      if (!slot) continue;

      const slotFrame = frameToPixels(slot.frame, context.viewSize);
      const fontSize = mmo.font.fontSize;
      const fontFamily = mmo.font.fontFamily || 'Inter';
      const lineHeightMultiplier = context.theme.typography.lineHeight || 1.4;
      const lineHeight = fontSize * lineHeightMultiplier;

      let estimatedTextHeight: number;

      try {
        const fontStr = `${fontSize}px ${fontFamily}`;
        const prepared = prepare(mmo.text, fontStr);
        const result = layout(prepared, slotFrame.width, lineHeight);
        estimatedTextHeight = result.height;
      } catch (e) {
        // Fallback to heuristic
        const avgCharWidth = fontSize * 0.5;
        const charsPerLine = Math.max(1, Math.floor(slotFrame.width / avgCharWidth));
        const paragraphs = mmo.text.split('\n');
        let lineCount = 0;
        for (const para of paragraphs) {
          lineCount += para.length === 0 ? 1 : Math.ceil(para.length / charsPerLine);
        }
        estimatedTextHeight = Math.max(1, lineCount) * lineHeight;
      }

      const slack = slotFrame.height - estimatedTextHeight;

      // Only center if slack > 20% of slot height AND > 20px
      if (slack <= slotFrame.height * 0.2 || slack <= 20) continue;

      const shift = slack / 2;
      mmo.frame.y = slotFrame.y + shift;
      mmo.frame.height = estimatedTextHeight;
    }
  }
}
